import React, { useMemo, useState } from 'react';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';

const formatMoney = (value) =>
    Number(value || 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    });

const CoinBalanceTrendPanel = ({ data, loading = false, appT = (value) => value }) => {
    const [metric, setMetric] = useState('comparable_total');
    const points = Array.isArray(data?.trend?.points) ? data.trend.points : [];
    const lifecycle = data?.lifecycle_summary || {};
    const metricOptions = useMemo(() => [
        { label: appT('Comparable fleet'), value: 'comparable_total' },
        { label: appT('Reported total'), value: 'reported_total' },
        { label: appT('Average per reporting machine'), value: 'average_per_reporting' }
    ], [appT]);
    const chartData = useMemo(() => ({
        labels: points.map((point) => point.date),
        datasets: [
            {
                label: appT(metricOptions.find((option) => option.value === metric)?.label || 'Balance'),
                data: points.map((point) => point.reporting_machines ? point[metric] : null),
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.14)',
                pointRadius: points.length > 45 ? 1 : 3,
                pointHoverRadius: 5,
                fill: true,
                tension: 0.25,
                spanGaps: false,
                yAxisID: 'balance'
            },
            {
                label: appT('Reporting machines'),
                data: points.map((point) => point.reporting_machines || null),
                borderColor: '#64748b',
                borderDash: [6, 5],
                backgroundColor: 'transparent',
                pointRadius: 0,
                tension: 0.2,
                yAxisID: 'machines'
            }
        ]
    }), [appT, metric, metricOptions, points]);
    const chartOptions = useMemo(() => ({
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: (context) => context.dataset.yAxisID === 'machines'
                        ? `${context.dataset.label}: ${Number(context.parsed.y || 0).toLocaleString()}`
                        : `${context.dataset.label}: ${formatMoney(context.parsed.y)}`
                }
            }
        },
        scales: {
            balance: {
                position: 'left',
                ticks: { callback: (value) => `$${Number(value).toLocaleString()}` }
            },
            machines: {
                position: 'right',
                beginAtZero: true,
                grid: { drawOnChartArea: false },
                ticks: { precision: 0 }
            },
            x: { ticks: { maxTicksLimit: 12 } }
        }
    }), []);

    if (loading && !data) {
        return <div className="flex justify-content-center py-6"><ProgressSpinner style={{ width: '44px', height: '44px' }} /></div>;
    }
    if (!points.some((point) => point.reporting_machines)) {
        return <div className="text-color-secondary py-4">{appT('No historical coin balance readings are available for this period.')}</div>;
    }

    return (
        <div>
            <div className="flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                <SelectButton
                    value={metric}
                    options={metricOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => event.value && setMetric(event.value)}
                />
                <div className="flex flex-wrap gap-2">
                    <Tag value={`${appT('Comparable machines')}: ${data?.trend?.comparable_machine_count || 0}`} severity="info" />
                    <Tag value={`${appT('Installed machines')}: ${lifecycle.installed || 0} · ${appT('Confirmed')} ${lifecycle.confirmed_installed || 0}`} severity="success" />
                    <Tag value={`${appT('Removed or persistently absent')}: ${lifecycle.removed || 0} · ${appT('Confirmed')} ${lifecycle.confirmed_removed || 0}`} severity="warning" />
                    <Tag value={`${appT('Temporarily missing')}: ${lifecycle.temporarily_missing || 0}`} severity="info" />
                </div>
            </div>
            <div style={{ height: '360px' }}>
                <Chart type="line" data={chartData} options={chartOptions} />
            </div>
            <div className="grid mt-2">
                <div className="col-12 md:col-4">
                    <small className="text-color-secondary">{appT('Installed balance entering the fleet')}</small>
                    <div className="font-semibold text-green-600">+{formatMoney(lifecycle.installed_balance)}</div>
                </div>
                <div className="col-12 md:col-4">
                    <small className="text-color-secondary">{appT('Last balance of removed or persistently absent machines')}</small>
                    <div className="font-semibold text-orange-600">{formatMoney(lifecycle.removed_balance)}</div>
                </div>
                <div className="col-12 md:col-4">
                    <small className="text-color-secondary">{appT('Historical coverage')}</small>
                    <div className="font-semibold">
                        {data?.trend?.coverage?.days_with_readings || 0} / {data?.trend?.coverage?.requested_days || 0} {appT('days')}
                    </div>
                </div>
            </div>
            <small className="block text-color-secondary mt-2">
                {appT('Comparable fleet includes machines reporting at both ends of the selected period. Inventory changes do not automatically become coin-loss observations.')}
            </small>
        </div>
    );
};

export default CoinBalanceTrendPanel;
